import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { DATA_STORAGE_CONVERTER_STRINGS } from "@/components/tools/data-storage-converter";
import { t } from "@/lib/i18n/interpolate";
import { superscript } from "@/lib/tool-kit/cs-tools/bits";
import {
  counterpart,
  type Duration,
  formatRational,
  fromBits,
  gapTable,
  type Rational,
  splitDuration,
  UNITS,
  type Unit,
  type UnitId,
  unitOf,
} from "@/lib/tool-kit/cs-tools/storage";

export type Strings = typeof DATA_STORAGE_CONVERTER_STRINGS;
export type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

const SI_BYTE_IDS: UnitId[] = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
const IEC_BYTE_IDS: UnitId[] = [
  "KiB",
  "MiB",
  "GiB",
  "TiB",
  "PiB",
  "EiB",
  "ZiB",
  "YiB",
];
const YEARS_SCIENTIFIC = 1e9;

export const NAME_KEYS: Record<UnitId, keyof Strings> = {
  bit: "nameBit",
  B: "nameB",
  kB: "nameKB",
  MB: "nameMB",
  GB: "nameGB",
  TB: "nameTB",
  PB: "namePB",
  EB: "nameEB",
  ZB: "nameZB",
  YB: "nameYB",
  KiB: "nameKiB",
  MiB: "nameMiB",
  GiB: "nameGiB",
  TiB: "nameTiB",
  PiB: "namePiB",
  EiB: "nameEiB",
  ZiB: "nameZiB",
  YiB: "nameYiB",
  kbit: "nameKbit",
  Mbit: "nameMbit",
  Gbit: "nameGbit",
  Tbit: "nameTbit",
  Pbit: "namePbit",
  Kibit: "nameKibit",
  Mibit: "nameMibit",
  Gibit: "nameGibit",
  Tibit: "nameTibit",
  Pibit: "namePibit",
};

/** A rational as locale text, with a power of ten when it is too small to write plainly. */
export function rationalText(
  r: Rational,
  locale: string,
  significant = 10
): string {
  const f = formatRational(r, locale, significant);
  if (f.exponent === null) {
    return f.text;
  }
  const sign = f.exponent < 0 ? "⁻" : "";
  return `${f.text} × 10${sign}${superscript(Math.abs(f.exponent))}`;
}

function unitRows(
  ids: readonly UnitId[],
  bits: Rational,
  strings: Strings,
  locale: string
): string[][] {
  return ids.map((id) => [
    id,
    strings[NAME_KEYS[id]],
    rationalText(fromBits(bits, unitOf(id)), locale),
  ]);
}

export function ConversionTables({
  bits,
  strings,
  locale,
}: {
  bits: Rational;
  strings: Strings;
  locale: string;
}) {
  const head = [strings.colUnit, strings.colName, strings.colValue];
  const groups: { caption: string; ids: UnitId[] }[] = [
    { caption: strings.groupBase, ids: ["bit", "B"] },
    { caption: strings.groupSiBytes, ids: SI_BYTE_IDS },
    { caption: strings.groupIecBytes, ids: IEC_BYTE_IDS },
    {
      caption: strings.groupBits,
      ids: UNITS.filter((u) => u.kind === "bit" && u.system !== "base").map(
        (u) => u.id
      ),
    },
  ];
  return (
    <section className="space-y-4">
      <h3 className={WIDGET_H3}>{strings.tablesHeading}</h3>
      {groups.map((group) => (
        <DataTable
          caption={group.caption}
          head={head}
          key={group.caption}
          rows={unitRows(group.ids, bits, strings, locale)}
        />
      ))}
    </section>
  );
}

export function ConversionStats({
  bits,
  unit,
  strings,
  locale,
}: {
  bits: Rational;
  unit: Unit;
  strings: Strings;
  locale: string;
}) {
  const other = counterpart(unit.id);
  return (
    <StatGrid>
      <StatCard
        emphasis
        label={strings.statBytes}
        value={rationalText(fromBits(bits, unitOf("B")), locale)}
      />
      <StatCard
        label={strings.statBits}
        value={rationalText(fromBits(bits, unitOf("bit")), locale)}
      />
      {other ? (
        <StatCard
          label={t(strings.statCounterpart, {
            from: unit.id,
            system:
              other.system === "iec" ? strings.systemIec : strings.systemSi,
          })}
          value={`${rationalText(fromBits(bits, other), locale, 6)} ${other.id}`}
        />
      ) : null}
    </StatGrid>
  );
}

export function GapTable({
  strings,
  fmt,
  locale,
}: {
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const rows = gapTable().map((gap, index) => [
    `${SI_BYTE_IDS[index]} / ${IEC_BYTE_IDS[index]}`,
    gap.si.toLocaleString(locale),
    gap.iec.toLocaleString(locale),
    `${fmt(gap.shortfall, { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%`,
  ]);
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.gapHeading}</h3>
      <p className="text-muted-foreground text-sm">{strings.gapBody}</p>
      <DataTable
        caption={strings.gapCaption}
        head={[
          strings.colPair,
          strings.colSiBytes,
          strings.colIecBytes,
          strings.colShortfall,
        ]}
        rows={rows}
      />
    </section>
  );
}

function durationText(total: number, strings: Strings, fmt: Fmt): string {
  if (total < 0.001) {
    return strings.durUnderMs;
  }
  if (total < 1) {
    return t(strings.durMs, {
      n: fmt(total * 1000, { maximumSignificantDigits: 3 }),
    });
  }
  if (total < 60) {
    return t(strings.durSeconds, {
      n: fmt(total, { maximumFractionDigits: 2 }),
    });
  }
  const d: Duration = splitDuration(Math.round(total));
  const parts: string[] = [];
  const whole: Intl.NumberFormatOptions = {
    maximumFractionDigits: 0,
    useGrouping: true,
  };
  if (d.years > 0) {
    const years =
      d.years >= YEARS_SCIENTIFIC
        ? fmt(d.years, { notation: "scientific", maximumFractionDigits: 2 })
        : fmt(d.years, whole);
    parts.push(t(strings.durYears, { n: years }));
    if (d.years < YEARS_SCIENTIFIC && d.days > 0) {
      parts.push(t(strings.durDays, { n: fmt(d.days, whole) }));
    }
    return parts.join(" ");
  }
  const fields: [number, keyof Strings][] = [
    [d.days, "durDays"],
    [d.hours, "durHours"],
    [d.minutes, "durMinutes"],
    [d.seconds, "durSeconds"],
  ];
  const first = fields.findIndex(([value]) => value > 0);
  for (const [value, key] of fields.slice(first, first + 3)) {
    if (value > 0) {
      parts.push(t(strings[key], { n: fmt(value, whole) }));
    }
  }
  return parts.join(" ");
}

export function TransferResult({
  seconds,
  rateBits,
  strings,
  fmt,
  locale,
}: {
  seconds: Rational;
  /** The effective speed in bits per second, after efficiency. */
  rateBits: Rational;
  strings: Strings;
  fmt: Fmt;
  locale: string;
}) {
  const total = Number(seconds.n) / Number(seconds.d);
  const speeds: UnitId[] = ["Mbit", "MB", "MiB"];
  return (
    <div className="space-y-4">
      <StatGrid>
        <StatCard
          emphasis
          label={strings.statTime}
          value={durationText(total, strings, fmt)}
        />
        <StatCard
          label={strings.statSeconds}
          value={rationalText(seconds, locale, 8)}
        />
      </StatGrid>
      <DataTable
        caption={strings.speedCaption}
        head={[strings.colUnit, strings.colName, strings.colSpeed]}
        rows={speeds.map((id) => [
          `${id}/s`,
          strings[NAME_KEYS[id]],
          rationalText(fromBits(rateBits, unitOf(id)), locale, 6),
        ])}
      />
    </div>
  );
}
